import { createRpc } from '@lightprotocol/stateless.js';
import { PublicKey } from '@solana/web3.js';
import { useAtom, useAtomValue } from 'jotai';
import { useMemo } from 'react';

import {
  compressedBalanceAtom,
  decompressedBalanceAtom,
  errorAtom,
  isAirdroppingAtom,
  isClaimingAtom,
  isDecompressingAtom,
  successAtom,
} from '@claim/atoms';

import { useFetchBalance } from './utils/use-fetch-balance';
import { useHandleDecompress } from './utils/use-handle-decompress';

/**
 * Custom hook that provides state and functions related to claiming an airdrop.
 * It manages decompression and simulates an airdrop using the user's Solana claim address.
 *
 * @returns An object containing error, success, isClaiming, isAirdropping, isDecompressing,
 *          compressedBalance, decompressedBalance, handleDecompress, handleSimulateAirdrop, and setIsClaiming.
 */
export const useClaim = () => {
  // Retrieve atom values for UI state
  const error = useAtomValue(errorAtom);
  const [success, setSuccess] = useAtom(successAtom);
  const isAirdropping = useAtomValue(isAirdroppingAtom);
  const isDecompressing = useAtomValue(isDecompressingAtom);
  const [isClaiming, setIsClaiming] = useAtom(isClaimingAtom);
  const compressedBalance = useAtomValue(compressedBalanceAtom);
  const decompressedBalance = useAtomValue(decompressedBalanceAtom);

  // Constants for RPC connection and mint address
  const RPC_URL = process.env.NEXT_PUBLIC_SOLANA_RPC_URL;
  const TOKEN_ADDRESS = process.env.NEXT_PUBLIC_DOOD_TOKEN_ADDRESS!;

  // Memoize the RPC connection to avoid re-instantiation on every render
  const connection = useMemo(
    () => createRpc(RPC_URL, RPC_URL, RPC_URL),
    [RPC_URL],
  );

  // Memoize the mint address
  const mintAddress = useMemo(
    () => new PublicKey(TOKEN_ADDRESS),
    [TOKEN_ADDRESS],
  );

  // Retrieve functions from custom hooks for handling decompression and airdrop simulation
  const { handleDecompress } = useHandleDecompress();
  const { fetchBalance } = useFetchBalance();

  return {
    error,
    success,
    setSuccess,
    isClaiming,
    isAirdropping,
    isDecompressing,
    compressedBalance,
    decompressedBalance,
    /**
     * Initiates the decompression process using the user's address, mint address, and connection.
     */
    handleDecompress: (userAddress: string) =>
      handleDecompress(userAddress, mintAddress, connection),
    fetchBalance: (userAddress: string) =>
      fetchBalance(userAddress, mintAddress, connection),
    setIsClaiming,
    mintAddress,
    connection,
  };
};
